import React, { useState, useEffect } from 'react';
import { db, doc, onSnapshot } from '../services/firebase';

/**
 * Displays upcoming special/holiday hours from Firestore (next 2 weeks).
 * Reads from settings/whapaxx → storeHoursCustom array.
 *
 * Props:
 *   variant: 'compact' (footer) | 'full' (contact page)
 */

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime12(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function SpecialHours({ variant = 'compact' }) {
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'whapaxx'), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const customDates = data.storeHoursCustom || [];

      // Filter to next 14 days
      const now = new Date();
      // Use Barbados date for "today"
      const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Barbados' }).format(now);
      const todayMs = new Date(todayStr + 'T00:00:00').getTime();
      const twoWeeksMs = todayMs + 14 * 24 * 60 * 60 * 1000;

      const filtered = customDates
        .filter(c => {
          const dateMs = new Date(c.date + 'T00:00:00').getTime();
          return dateMs >= todayMs && dateMs <= twoWeeksMs;
        })
        .sort((a, b) => a.date.localeCompare(b.date));

      setUpcoming(filtered);
    }, (err) => {
      console.warn('[SpecialHours] Firestore error:', err);
    });
    return () => unsub();
  }, []);

  if (upcoming.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div style={compactStyles.wrapper}>
        <h4 style={compactStyles.title}>Special Hours</h4>
        {upcoming.map(cd => (
          <div key={cd.date} style={compactStyles.row}>
            <span style={compactStyles.date}>
              {formatDate(cd.date)}
              {cd.label ? ` — ${cd.label}` : ''}
            </span>
            <span style={cd.enabled ? compactStyles.hours : compactStyles.closed}>
              {cd.enabled ? `${formatTime12(cd.open)} – ${formatTime12(cd.close)}` : 'Closed'}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Full variant for contact page
  return (
    <div style={fullStyles.wrapper}>
      <div style={fullStyles.icon}>📅</div>
      <h4 style={fullStyles.title}>Upcoming Special Hours</h4>
      {upcoming.map(cd => (
        <div key={cd.date} style={fullStyles.row}>
          <div style={fullStyles.dateCol}>
            <span style={fullStyles.date}>{formatDate(cd.date)}</span>
            {cd.label && <span style={fullStyles.label}>{cd.label}</span>}
          </div>
          <span style={cd.enabled ? fullStyles.hours : fullStyles.closed}>
            {cd.enabled ? `${formatTime12(cd.open)} – ${formatTime12(cd.close)}` : 'Closed'}
          </span>
        </div>
      ))}
    </div>
  );
}

const compactStyles = {
  wrapper: {
    marginTop: '1rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid rgba(18, 208, 255, 0.08)',
  },
  title: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    color: '#ffc857',
    marginBottom: '0.5rem',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '0.3rem',
    gap: '0.5rem',
  },
  date: {
    color: '#a8b2ff',
    fontSize: '0.8rem',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 500,
  },
  hours: {
    color: '#22c55e',
    fontSize: '0.8rem',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  closed: {
    color: '#e52b39',
    fontSize: '0.8rem',
    fontFamily: "'Orbitron', sans-serif",
    fontWeight: 600,
    letterSpacing: '0.1em',
    whiteSpace: 'nowrap',
  },
};

const fullStyles = {
  wrapper: {
    marginTop: '0.75rem',
    padding: '1rem',
    background: 'rgba(255, 200, 87, 0.04)',
    border: '1px solid rgba(255, 200, 87, 0.15)',
    borderRadius: '8px',
  },
  icon: {
    fontSize: '1.2rem',
    marginBottom: '0.5rem',
  },
  title: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    color: '#ffc857',
    marginBottom: '0.75rem',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0',
    borderBottom: '1px solid rgba(255, 200, 87, 0.06)',
  },
  dateCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.1rem',
  },
  date: {
    color: '#eaf2ff',
    fontSize: '0.9rem',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
  },
  label: {
    color: '#a8b2ff',
    fontSize: '0.75rem',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 500,
    fontStyle: 'italic',
  },
  hours: {
    color: '#22c55e',
    fontSize: '0.9rem',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  closed: {
    color: '#e52b39',
    fontSize: '0.8rem',
    fontFamily: "'Orbitron', sans-serif",
    fontWeight: 600,
    letterSpacing: '0.1em',
    whiteSpace: 'nowrap',
  },
};
